import { infraTaskAPI } from '@/api/storeManagement/registration';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ApLockCheckState = {
    enableWakeUp: boolean;
    enableSwitchChannel: boolean;
};

const DEFAULT_CHECK_STATE: ApLockCheckState = {
    enableWakeUp: true,
    enableSwitchChannel: true,
};

export const useToggleApLock = () => {
    const { t } = useTranslation('common');
    const { state, dispatch } = useRegistrationState();
    const { refreshTable } = useRegistrationLifecycleState();
    const [checkState, setCheckState] = useState<ApLockCheckState>(DEFAULT_CHECK_STATE);

    const closeApLockDialog = useCallback(() => {
        dispatch({
            type: 'TOGGLE_DIALOG',
            payload: {
                dialog: 'isApLockOpen',
                isOpen: false,
            },
        });
    }, [dispatch]);

    const resetCheckState = useCallback(() => {
        setCheckState(DEFAULT_CHECK_STATE);
    }, []);

    const executeAPTask = useCallback(async (mac: string, task: string, action: string) => {
        await infraTaskAPI({
            mac,
            task,
            action,
        });
    }, []);

    const confirmApLockChange = useCallback(async () => {
        const { isLock, mac } = state.apLockInfo;

        closeApLockDialog();

        dispatch({
            type: 'SET_TABLE_STATE',
            payload: {
                doingTask: true,
            },
        });
        try {
            if (!isLock) {
                await executeAPTask(mac, 'trifecta', 'stop');
                return;
            }

            if (checkState.enableSwitchChannel && checkState.enableWakeUp) {
                await executeAPTask(mac, 'trifecta', 'start');
                return;
            }

            await executeAPTask(mac, 'network_mode', 'unlock');

            if (checkState.enableSwitchChannel) {
                await executeAPTask(mac, 'switch_channel', 'start,from,81');
            }

            if (checkState.enableWakeUp) {
                await executeAPTask(mac, 'wakeup_mode', 'start');
            }
        } finally {
            dispatch({
                type: 'SET_TABLE_STATE',
                payload: {
                    doingTask: false,
                },
            });
            void refreshTable({ clearSelected: true });
        }
    }, [
        checkState.enableSwitchChannel,
        checkState.enableWakeUp,
        closeApLockDialog,
        dispatch,
        executeAPTask,
        state.apLockInfo,
        refreshTable,
    ]);

    const handleCheckChange = useCallback((key: keyof ApLockCheckState) => {
        return (event: ChangeEvent<HTMLInputElement>) => {
            setCheckState((prev) => ({
                ...prev,
                [key]: event.target.checked,
            }));
        };
    }, []);

    const actionText = useMemo(() => {
        return state.apLockInfo.isLock ? t('unlock') : t('lock');
    }, [state.apLockInfo.isLock, t]);

    const confirmMessage = useMemo(() => {
        return t('Are you sure you want to {{action}} [ {{mac}} ] ?', {
            action: actionText,
            mac: state.apLockInfo.mac,
        });
    }, [actionText, state.apLockInfo.mac, t]);

    const title = useMemo(() => {
        return state.apLockInfo.isLock ? t('Unlock Access Point') : t('Lock Access Point');
    }, [state.apLockInfo.isLock, t]);
    return {
        state,
        checkState,
        confirmMessage,
        closeApLockDialog,
        confirmApLockChange,
        handleCheckChange,
        resetCheckState,
        title,
    };
};
